/*
  Listing navigation helpers
  --------------------------
  1. Scroll restoration for listing pages (collections, search, homepage)
     - On a product-card click, captures the current listing URL + scrollY
       in sessionStorage under STORAGE_KEY.
     - On listing page load, if the stored URL matches the current URL,
       restores scrollY (after layout settles) and clears the entry.
     - Also listens for `pageshow` to handle bfcache restores cleanly.

  2. Smart back-link resolver
     - On product page load, if a stored entry exists with a referrer URL
       and label, rewrites the [data-fallback-href] back-link to point
       there instead of the static fallback.
*/

(function () {
  'use strict';

  var STORAGE_KEY = 'sss:listing-nav';
  var LISTING_PATH_RE = /^\/(collections|search|products)(\/|$|\?)|^\/$/;

  function isListingPage() {
    var path = window.location.pathname;
    if (path === '/' || path === '/search') return true;
    if (path.indexOf('/collections') === 0 && path.indexOf('/products/') === -1) return true;
    return false;
  }

  function isProductPage() {
    return window.location.pathname.indexOf('/products/') !== -1;
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* sessionStorage full or unavailable — silent */ }
  }

  function clearState() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function deriveListingLabel() {
    var path = window.location.pathname;
    if (path === '/') return 'Home';
    if (path === '/search') return 'Search Results';

    var collectionMatch = path.match(/^\/collections\/([^/]+)/);
    if (collectionMatch) {
      var heading = document.querySelector('.collection__title, .main-collection__title, h1');
      if (heading && heading.textContent.trim()) return heading.textContent.trim();
      return collectionMatch[1].replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }
    return null;
  }

  // ── 1a. Capture scroll on product-card click ─────────────────────────
  function bindCapture() {
    if (!isListingPage()) return;

    document.addEventListener('click', function (event) {
      var anchor = event.target.closest && event.target.closest('a[href*="/products/"]');
      if (!anchor) return;
      // Only same-tab navigations
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
      if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return;

      var label = deriveListingLabel();
      writeState({
        url: window.location.pathname + window.location.search,
        scrollY: window.scrollY || window.pageYOffset || 0,
        label: label,
        ts: Date.now()
      });
    }, { capture: true });
  }

  // ── 1b. Restore scroll on listing page load ──────────────────────────
  function restoreScroll() {
    if (!isListingPage()) return;
    var state = readState();
    if (!state || !state.url) return;

    var here = window.location.pathname + window.location.search;
    if (state.url !== here) return;

    // Disable browser auto-restore so we control the final position.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    var target = state.scrollY || 0;
    var attempts = 0;
    function tryScroll() {
      window.scrollTo(0, target);
      attempts += 1;
      // Layout may shift as images/fonts load; re-snap a few times.
      if (attempts < 6 && Math.abs((window.scrollY || 0) - target) > 4) {
        setTimeout(tryScroll, 80);
      } else {
        clearState();
      }
    }
    // Wait one frame for the grid to paint.
    requestAnimationFrame(function () { setTimeout(tryScroll, 0); });
  }

  // ── 2. Back-link resolver on product page ────────────────────────────
  function resolveBackLink() {
    if (!isProductPage()) return;
    var nav = document.querySelector('.product-back-link');
    if (!nav) return;
    var anchor = nav.querySelector('.product-back-link__anchor');
    var labelEl = nav.querySelector('.product-back-link__label');
    if (!anchor || !labelEl) return;

    var state = readState();
    if (state && state.url && state.label) {
      anchor.href = state.url;
      labelEl.textContent = 'Back to ' + state.label;
      return;
    }

    // Fall back to document.referrer if it's same-origin and a listing.
    try {
      if (document.referrer) {
        var ref = new URL(document.referrer);
        if (ref.origin === window.location.origin) {
          var refPath = ref.pathname;
          var isListingRef = refPath === '/' || refPath === '/search' ||
            (refPath.indexOf('/collections') === 0 && refPath.indexOf('/products/') === -1);
          if (isListingRef) {
            anchor.href = ref.pathname + ref.search;
            if (refPath === '/') labelEl.textContent = 'Back to Home';
            else if (refPath === '/search') labelEl.textContent = 'Back to Search Results';
            // For collection referrers, keep the Liquid-rendered fallback label
            // (it already reflects the current product's collection context).
          }
        }
      }
    } catch (e) { /* malformed referrer — keep fallback */ }
  }

  // ── Wiring ──────────────────────────────────────────────────────────
  function init() {
    bindCapture();
    restoreScroll();
    resolveBackLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // bfcache restore: re-run scroll restoration when navigating back.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      restoreScroll();
      resolveBackLink();
    }
  });
})();
